import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs/Subscription';

import { first } from 'rxjs/operators';
import { UI_CONFIG } from '../ui.config';
import { UiConfig } from '../ui.interface';
import { IconService } from './icon.service';

export enum IconColor {
  DARK = 'dark',
  INHERIT = 'inherit',
  LIGHT = 'light',
  MID = 'mid',
  PRIMARY = 'primary',
  WHITE = 'white',
}

export enum HoverColor {
  CURRENT = 'currentColor',
  LIGHT = 'light',
  PRIMARY = 'primary',
}

export enum IconSize {
  MID = 'mid',
  SMALL = 'small',
}

export enum IconBackdrop {
  FREEMARKET = 'freemarket',
  PRIMARY = 'primary',
  PRIMARY_DARK = 'primary-dark',
  TERTIARY = 'tertiary',
  TRANSPARENT = 'transparent',
  WARRIOR_FORUM = 'warrior-forum',
}

@Component({
  selector: 'fl-icon',
  template: `
    <div class="IconBackdrop"
      [attr.data-color]="backdrop"
      [attr.data-size]="size"
    >
      <div #container
        class="IconContainer"
        [attr.data-color]="color"
        [attr.data-hover-color]="hoverColor"
        [attr.data-size]="size"
        [attr.data-legacy]="attrLegacy"
        [attr.aria-label]="label"
        [attr.title]="label"
        [attr.aria-hidden]="!attrAriaHidden"
      ></div>
    </div>
  `,
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent implements OnChanges, OnDestroy {
  attrLegacy?: true;
  attrAriaHidden?: true;
  iconSubscription?: Subscription;

  @Input() name: string;
  @Input() label: string;
  @Input() color = IconColor.DARK;
  @Input() hoverColor?: HoverColor;
  @Input() size = IconSize.MID;
  @Input() backdrop?: IconBackdrop = undefined;
  @Input()
  set legacy(value: boolean) {
    this.attrLegacy = value ? true : undefined;
  }
  @ViewChild('container') container: any;
  constructor(
    private renderer: Renderer2,
    private iconService: IconService,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {}

  private setElement(svg: SVGElement) {
    const layoutElement: Element = this.container.nativeElement;
    // Detach all views of the child nodes of the root element.
    // NOTE: This wouldn't work in IE 11 because there's a bug there that
    // causes SVG to not be included in the list.
    // We've polyfilled it though.
    // Ref: https://github.com/angular/angular/issues/6327
    Array.from(layoutElement.childNodes).forEach((node: Node) => {
      this.renderer.removeChild(layoutElement, node);
    });
    // Put the new icon SVG in.
    this.renderer.appendChild(layoutElement, svg);
  }

  ngOnChanges(changes: SimpleChanges) {
    const changedInputs = Object.keys(changes);
    if (changedInputs.includes('name')) {
      // Check if the new value is not an empty string or undefined.
      if (changes.name.currentValue) {
        this.iconSubscription = this.iconService
          .getIcon(
            `${this.uiConfig.assetsBaseUrl}/icons/${
              changes.name.currentValue
            }.svg`,
            this.renderer,
          )
          .pipe(first())
          .subscribe(svg => {
            this.setElement(svg);
          });
      }
    }

    if (changedInputs.includes('label')) {
      this.attrAriaHidden = changes.name.currentValue ? undefined : true;
    }
  }

  ngOnDestroy() {
    if (this.iconSubscription !== undefined) {
      this.iconSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/ui/icon/icon.component.ts